import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  ArrowDownToLine,
  Check,
  Download,
  FileStack,
  GripVertical,
  Loader2,
  Upload,
  X,
} from "lucide-react"
import { useState } from "react"

import { FileDropzone } from "@/components/FileDropzone"
import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { RelatedTools } from "@/components/related-tools"
import { SEO } from "@/components/seo"
import { Button } from "@/components/ui/button"
import { useDocumentUpload } from "@/hooks/useDocumentUpload"
import { useOperationGuard } from "@/hooks/useOperationGuard"
import { usePdfMerge } from "@/hooks/usePdfMerge"
import { type PdfFile, usePdfThumbnails } from "@/hooks/usePdfThumbnails"
import { cn } from "@/lib/utils"

const howItWorks = [
  { icon: Upload, text: "Upload PDFs" },
  { icon: GripVertical, text: "Drag to reorder" },
  { icon: ArrowDownToLine, text: "Download merged PDF" },
]

interface Props {
  limits: {
    daily_remaining: number
    max_file_size_mb: number
    max_pages: number
  }
  authenticated: boolean
}

function SortableFileThumbnail({
  file,
  onRemove,
}: {
  file: PdfFile
  onRemove: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: file.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "bg-card group relative cursor-grab rounded-lg border p-2 transition-shadow",
        isDragging && "ring-primary shadow-lg ring-2",
      )}
      {...attributes}
      {...listeners}
    >
      <button
        type="button"
        className="absolute -top-1.5 -right-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-gray-500 opacity-0 shadow-sm ring-1 ring-gray-200 transition-all group-hover:opacity-100 hover:bg-red-500 hover:text-white hover:ring-red-500 dark:bg-gray-700 dark:text-gray-400 dark:ring-gray-600 dark:hover:bg-red-500 dark:hover:text-white dark:hover:ring-red-500"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
      >
        <X className="h-3 w-3" />
      </button>
      <img
        src={file.thumbnails[0]}
        alt={file.name}
        className="h-32 w-auto rounded object-contain"
        draggable={false}
      />
      <p className="text-muted-foreground mt-1 max-w-[100px] truncate text-center text-xs">
        {file.name} ({file.pageCount}p)
      </p>
    </div>
  )
}

export default function Merge({ limits, authenticated }: Props) {
  const [files, setFiles] = useState<PdfFile[]>([])
  const [hasReordered, setHasReordered] = useState(false)
  const { generateThumbnails, loading: loadingThumbnails } = usePdfThumbnails()
  const { mergePdfs, processing, progress } = usePdfMerge()
  const { uploadDocument } = useDocumentUpload()
  const {
    remainingOperations,
    completed,
    guardOperation,
    markCompleted,
    Modals,
  } = useOperationGuard({
    dailyRemaining: limits.daily_remaining,
    authenticated,
  })

  const isClient = typeof window !== "undefined"

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleFilesAdded = async (newFiles: File[]) => {
    for (const file of newFiles) {
      const pdfFile = await generateThumbnails(file)
      setFiles((prev) => [...prev, pdfFile])
    }
  }

  const handleRemoveFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setFiles((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id)
        const newIndex = items.findIndex((i) => i.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
      setHasReordered(true)
    }
  }

  const handleMerge = async () => {
    if (files.length < 2) return
    if (!guardOperation()) return

    const blob = await mergePdfs(files)
    const filename = "merged.pdf"

    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)

    markCompleted()

    const result = await uploadDocument(blob, filename, "merge_pdfs")
    if (!result) {
      console.error("Failed to upload document")
    }
  }

  const totalPages = files.reduce((sum, f) => sum + f.pageCount, 0)

  return (
    <>
      <SEO
        title="Merge PDF Files Online - Free PDF Combiner | StitchPDF"
        description="Merge PDF files online in seconds - completely free. Drag and drop to combine multiple PDFs into one document. No signup required."
        canonical="https://stitchpdf.com/merge-pdf"
        ogTitle="Merge PDF Files Online - StitchPDF"
        ogDescription="Combine multiple PDF files into one document for free."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Merge PDF - StitchPDF",
            description:
              "Combine multiple PDF files into one document. Free online tool with drag and drop interface.",
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Web Browser",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Merge PDF Files",
            description:
              "Combine multiple PDF documents into a single file using StitchPDF.",
            step: [
              {
                "@type": "HowToStep",
                name: "Upload PDFs",
                text: "Drag and drop your PDF files or click to select them from your device.",
              },
              {
                "@type": "HowToStep",
                name: "Reorder files",
                text: "Drag files to arrange them in the order you want them merged.",
              },
              {
                "@type": "HowToStep",
                name: "Download merged PDF",
                text: "Click Merge & Download to get your combined PDF file.",
              },
            ],
          })}
        </script>
      </SEO>

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3">
              <FileStack className="text-primary h-8 w-8" aria-hidden="true" />
              <h1 className="text-2xl font-bold">Merge PDF</h1>
            </div>
            <p className="text-muted-foreground mt-2">
              Combine multiple PDF files into one document
            </p>

            <div className="mt-6 flex items-center justify-center gap-6 text-sm">
              {howItWorks.map((step, i) => {
                const hasFiles = files.length > 0
                const canMerge = files.length >= 2
                const isComplete =
                  completed ||
                  (hasFiles && i === 0) ||
                  (hasReordered && i === 1) ||
                  (processing && i <= 1)
                const isActive =
                  !completed &&
                  ((processing && i === 2) ||
                    (!processing &&
                      ((hasFiles && !hasReordered && i === 1) ||
                        (canMerge && i === 2))))
                return (
                  <div
                    key={step.text}
                    className={`flex items-center gap-2 ${
                      isComplete
                        ? "text-green-700 dark:text-green-400"
                        : isActive
                          ? "text-primary font-medium"
                          : "text-muted-foreground"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                        isComplete
                          ? "bg-green-200 text-green-700 dark:bg-green-900 dark:text-green-400"
                          : isActive
                            ? "bg-primary text-primary-foreground"
                            : "bg-primary/10 text-primary"
                      }`}
                    >
                      {isComplete ? (
                        <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        i + 1
                      )}
                    </span>
                    <step.icon className="h-4 w-4" aria-hidden="true" />
                    <span>{step.text}</span>
                  </div>
                )
              })}
            </div>

            {files.length === 0 && (
              <div className="mt-8">
                <FileDropzone
                  onFilesAdded={(newFiles) => void handleFilesAdded(newFiles)}
                />
              </div>
            )}

            {loadingThumbnails && (
              <div className="text-muted-foreground mt-4 flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading PDF...
              </div>
            )}

            {files.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {files.length} files, {totalPages} pages total
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {remainingOperations} operations remaining today
                  </p>
                </div>

                {isClient ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={files.map((f) => f.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="mt-4 flex flex-wrap gap-4">
                        {files.map((file) => (
                          <SortableFileThumbnail
                            key={file.id}
                            file={file}
                            onRemove={() => handleRemoveFile(file.id)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="mt-4 flex flex-wrap gap-4">
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="bg-card group relative rounded-lg border p-2"
                      >
                        <img
                          src={file.thumbnails[0]}
                          alt={file.name}
                          className="h-32 w-auto rounded object-contain"
                        />
                        <p className="text-muted-foreground mt-1 max-w-[100px] truncate text-center text-xs">
                          {file.name} ({file.pageCount}p)
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6">
                  <FileDropzone
                    onFilesAdded={(newFiles) => void handleFilesAdded(newFiles)}
                    compact
                  />
                </div>

                <div className="mt-8 flex gap-4">
                  <Button
                    onClick={() => void handleMerge()}
                    disabled={files.length < 2 || processing}
                    className="gap-2"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Merging... {Math.round(progress)}%
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Merge & Download
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            <RelatedTools currentTool="merge-pdf" />

            <section className="mt-16 border-t pt-12">
              <h2 className="text-xl font-semibold">About This Tool</h2>
              <div className="text-muted-foreground mt-4 space-y-4 text-sm leading-relaxed">
                <p>
                  Drag PDFs into the box above, reorder them, and download a
                  single combined file. The entire process runs in your browser.
                  Your files never leave your device.
                </p>
                <p>
                  No upload wait. No server processing. You can even use it
                  offline once the page loads.
                </p>
                <h3 className="text-foreground pt-4 font-medium">Features</h3>
                <ul className="list-inside list-disc space-y-2">
                  <li>
                    <strong>Browser processing:</strong> Files stay on your
                    device
                  </li>
                  <li>
                    <strong>Drag to reorder:</strong> Arrange files before
                    merging
                  </li>
                  <li>
                    <strong>Thumbnails:</strong> Preview pages and page counts
                  </li>
                  <li>
                    <strong>Large files:</strong> No size restrictions
                  </li>
                </ul>
                <h3 className="text-foreground pt-4 font-medium">Use Cases</h3>
                <p>
                  Combine invoices for record-keeping. Merge contract sections.
                  Assemble ebook chapters. Consolidate scanned pages into one
                  file.
                </p>
              </div>
            </section>
          </div>
        </main>
        <PublicFooter />
      </div>

      <Modals />
    </>
  )
}
